import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiUploadCloud } from 'react-icons/fi';
import { FaBroadcastTower } from 'react-icons/fa';
import api from '../services/api';

const CreateRadioStep2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const draft = JSON.parse(localStorage.getItem('radioDraft') || '{}');
  const [logo, setLogo] = useState(draft.logo || '');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError(t('createRadio.fileTooLarge')); return; }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await api.post('/api/uploads/logo', fd);
      setLogo(res.data.url);
    } catch (err) {
      setError(err?.response?.data?.message || t('createRadio.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleNext = () => {
    localStorage.setItem('radioDraft', JSON.stringify({ ...draft, logo }));
    navigate('/creer-radio/step3');
  };

  const handlePrevious = () => navigate('/creer-radio/step1');

  return (
    <div className="bg-gray-50 min-h-screen pt-28 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('createRadio.title')}</h1>
          <p className="text-gray-600 mt-1">{t('createRadio.subtitle')}</p>
          <div className="flex justify-center gap-4 mt-6">
            <span className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full">1</span>
            <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full font-semibold">2</div>
            <div className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-600 rounded-full">3</div>
          </div>
          <div className="flex justify-center gap-8 text-sm mt-2 text-gray-500">
            <span>{t('createRadio.stepInfo')}</span>
            <span className="text-purple-600 font-medium">{t('createRadio.stepCustom')}</span>
            <span>{t('createRadio.stepConfig')}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-8 max-w-3xl mx-auto">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{t('createRadio.stepCustom')}</h2>

        <label className="block text-gray-700 mb-2">{t('createRadio.logoLabel')}</label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
          <label className="cursor-pointer text-purple-600 flex flex-col items-center space-y-2">
            <FiUploadCloud className="text-4xl" />
            <span>{uploading ? t('createRadio.uploading') : t('createRadio.chooseFile')}</span>
            <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
            <p className="text-sm text-gray-500">{t('createRadio.fileHint')}</p>
          </label>
        </div>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

        <div className="bg-purple-50 p-4 mt-6 rounded-xl">
          <p className="font-semibold text-gray-800 mb-2">{t('createRadio.preview')}</p>
          <div className="flex items-center space-x-4">
            {logo ? (
              <img src={logo} alt="" className="w-12 h-12 rounded-full object-cover bg-white"
                onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full flex items-center justify-center text-2xl">
                <FaBroadcastTower />
              </div>
            )}
            <div>
              <p className="font-bold text-gray-900">{draft.name || t('createRadio.previewName')}</p>
              <p className="text-sm text-gray-600">
                {(draft.genre || t('createRadio.previewGenre'))} • {(draft.language || t('createRadio.previewLanguage'))}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <button onClick={handlePrevious} className="bg-white border border-gray-300 text-gray-700 px-5 py-2 rounded-md hover:bg-gray-50">
            {t('createRadio.previous')}
          </button>
          <button onClick={handleNext} disabled={uploading}
            className="bg-gradient-to-r from-purple-500 to-blue-500 text-white px-6 py-2 rounded-md hover:from-purple-600 hover:to-blue-600 disabled:opacity-60">
            {t('createRadio.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateRadioStep2;
